import { forwardRef, useState, useEffect } from 'react';
import { Textarea as HeadlessTextarea } from '@headlessui/react';
import { FieldLabel } from './FieldLabel';

export type TextareaProps = {
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur?: () => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  label?: string;
  /** Help text shown as a `?` tooltip beside the label. */
  helpText?: string;
  description?: string;
  error?: string;
  disabled?: boolean;
  rows?: number;
  minLength?: number;
  maxLength?: number;
  className?: string;
  showCharCount?: boolean;
  resize?: 'none' | 'vertical' | 'horizontal' | 'both';
  size?: 'small' | 'medium';
  autoGrow?: boolean;
  minRows?: number;
  maxRows?: number;
  autoFocus?: boolean;
  'aria-label'?: string;
  /** Regex pattern for inline validation on blur. */
  regex?: string;
  /** Error message shown when regex validation fails. */
  regexError?: string;
};

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(({
  value,
  onChange,
  onBlur,
  onKeyDown,
  placeholder,
  label,
  helpText,
  description,
  error,
  disabled = false,
  rows = 4,
  minLength,
  maxLength,
  className = '',
  showCharCount = false,
  resize = 'vertical',
  size = 'medium',
  autoGrow = false,
  minRows = 1,
  maxRows,
  autoFocus = false,
  'aria-label': ariaLabel,
  regex,
  regexError = 'Invalid format',
}, forwardedRef) => {
  const [calculatedRows, setCalculatedRows] = useState(rows);
  const [regexErrMsg, setRegexErrMsg] = useState<string | null>(null);
  const [minLengthErr, setMinLengthErr] = useState<string | null>(null);

  const displayError = error || regexErrMsg || minLengthErr;

  // Auto-grow: Calculate rows based on line count
  useEffect(() => {
    if (autoGrow && value !== undefined) {
      const lineCount = value.split('\n').length;
      const newRows = Math.max(minRows, lineCount);
      const finalRows = maxRows ? Math.min(newRows, maxRows) : newRows;
      setCalculatedRows(finalRows);
    }
  }, [value, autoGrow, minRows, maxRows]);

  const resizeClasses = {
    none: 'resize-none',
    vertical: 'resize-y',
    horizontal: 'resize-x',
    both: 'resize',
  };

  const charCount = value?.length || 0;
  const showCount = showCharCount || maxLength !== undefined;

  const handleBlur = (): void => {
    // Validate regex on blur
    if (regex && value) {
      try {
        const re = new RegExp(regex);
        if (!re.test(value)) {
          setRegexErrMsg(regexError);
        } else {
          setRegexErrMsg(null);
        }
      } catch {
        setRegexErrMsg(null);
      }
    } else {
      setRegexErrMsg(null);
    }
    // Validate minLength on blur
    if (minLength !== undefined && minLength !== null && value && value.length < minLength) {
      setMinLengthErr(`Must be at least ${minLength} characters`);
    } else {
      setMinLengthErr(null);
    }
    onBlur?.();
  };

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <FieldLabel className="block text-sm font-medium text-white/90 mb-2" helpText={helpText}>
          {label}
        </FieldLabel>
      )}

      {description && (
        <p className="text-xs text-white/60 font-light mb-2">{description}</p>
      )}

      <div className="relative">
        <HeadlessTextarea
          ref={forwardedRef}
          value={value}
          onChange={onChange}
          onBlur={handleBlur}
          onKeyDown={onKeyDown}
          aria-label={ariaLabel}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus={autoFocus}
          rows={autoGrow ? calculatedRows : rows}
          maxLength={maxLength}
          className={`
            input-base
            ${autoGrow ? 'resize-none' : resizeClasses[resize]}
            ${size === 'small' ? 'px-3 py-2 text-xs' : 'px-4 py-2.5 text-sm'}
            ${displayError ? 'input-error' : ''}
            data-focus:bg-white/10
          `}
        />

      </div>

      {showCount && (
        <div className="mt-1 text-right text-xs text-white/40">
          {maxLength !== undefined ? `${charCount}/${maxLength}` : charCount}
        </div>
      )}

      {displayError && (
        <p className="mt-2 text-xs text-red-400 font-light flex items-center gap-1">
          <svg
            className="h-4 w-4"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 10a1 1 0 100-2 1 1 0 000 2z"
              clipRule="evenodd"
            />
          </svg>
          {displayError}
        </p>
      )}
    </div>
  );
});

Textarea.displayName = 'Textarea';
